import { ENV } from "../../Utils";

function extOf(name = "") {
  const n = String(name).toLowerCase();
  const i = n.lastIndexOf(".");
  return i >= 0 ? n.slice(i + 1) : "";
}

// Mínima allowlist / normalización por extensión
function normalizeContentType(file) {
  const name = file?.name || "";
  const ext = extOf(name);

  // Prioridad 1: si el navegador da un type “bueno”, úsalo
  const t = String(file?.type || "")
    .toLowerCase()
    .trim();

  // Algunos navegadores/devices pueden dar "audio/mp3" (no estándar)
  if (t === "audio/mp3") return "audio/mpeg";

  // Si ya es uno de los tipos esperables, lo dejamos
  const allowed = new Set([
    "video/mp4",
    "video/webm",
    "image/jpeg",
    "image/png",
    "image/webp",
    "audio/mpeg",
    "audio/ogg",
    "audio/wav",
    "audio/aac",
    "audio/mp4",
  ]);
  if (allowed.has(t)) return t;

  // Prioridad 2: inferir por extensión (más fiable que file.type)
  const byExt = {
    mp4: "video/mp4",
    webm: "video/webm",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    mp3: "audio/mpeg",
    ogg: "audio/ogg",
    wav: "audio/wav",
    aac: "audio/aac",
    m4a: "audio/mp4",
  };
  if (byExt[ext]) return byExt[ext];

  // Último recurso: octet-stream (puede fallar si backend valida estricto)
  return "application/octet-stream";
}

export class Video360Api {
  baseApi = ENV.BASE_API;

  _rootBase() {
    return String(this.baseApi || "")
      .replace(/\/revi\/?$/i, "")
      .replace(/\/$/, "");
  }

  _reviBase() {
    return `${this._rootBase()}/revi`;
  }

  async _fetchJson(url, params) {
    const r = await fetch(url, params);
    const t = await r.text();
    let j = {};
    try {
      j = t ? JSON.parse(t) : {};
    } catch {
      j = { raw: t };
    }
    if (!r.ok) {
      const msg = j?.message || j?.error || `HTTP ${r.status}`;
      const err = new Error(msg);
      err.status = r.status;
      err.payload = j;
      throw err;
    }
    return j;
  }

  async list(accessToken, { pageToken } = {}) {
    const url = new URL(`${this._reviBase()}/gcs/list`);
    if (pageToken) url.searchParams.set("pageToken", pageToken);
    return this._fetchJson(url.toString(), {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  async createUploadUrl(accessToken, { title, type, filename, contentType }) {
    return this._fetchJson(`${this._reviBase()}/gcs/upload-url`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ title, type, filename, contentType }),
    });
  }

  /**
   * Upload directo a GCS (PUT signed URL) con progreso.
   * Devuelve { name, publicUrl, size } para mantener compat con tu controller.
   */
  async upload(accessToken, { file, title, type, onProgress }) {
    const filename = file?.name || "file.bin";
    const contentType = normalizeContentType(file);

    // Log útil (sin magia) para debug de backend
    // eslint-disable-next-line no-console
    console.info("[Media360] createUploadUrl payload", {
      title: title || filename,
      type: type || "360",
      filename,
      contentType,
      browserFileType: file?.type || "",
    });

    const signed = await this.createUploadUrl(accessToken, {
      title: title || filename,
      type: type || "360",
      filename,
      contentType,
    });

    const {
      uploadUrl,
      publicUrl,
      name,
      contentType: signedContentType,
    } = signed;

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);

      // Debe coincidir con el Content-Type firmado
      xhr.setRequestHeader("Content-Type", signedContentType || contentType);

      xhr.timeout = 60 * 60 * 1000;

      xhr.upload.onprogress = (evt) => {
        if (!onProgress) return;
        const total = evt.total || file.size || 0;
        const loaded = evt.loaded || 0;
        const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
        // ✅ Firma esperada por el controller: (pct, known)
        onProgress(percent, total > 0);
      };

      xhr.onerror = () =>
        reject(new Error("Error de red durante la subida (GCS)"));
      xhr.ontimeout = () =>
        reject(new Error("Timeout durante la subida (GCS)"));

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        reject(new Error(`Error subiendo a GCS (HTTP ${xhr.status})`));
      };

      xhr.send(file);
    });

    return { name, publicUrl, size: file.size };
  }

  async remove(accessToken, name) {
    return this._fetchJson(`${this._reviBase()}/gcs/object`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ name }),
    });
  }

  async rename(accessToken, from, to) {
    return this._fetchJson(`${this._reviBase()}/gcs/object/rename`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ from, to }),
    });
  }

  inferModeFromName(name) {
    if (!name) return "mono";
    const n = name.toLowerCase();
    if (n.includes("__tb.")) return "3d-tb";
    if (n.includes("__lr.")) return "3d-sbs";
    return "mono";
  }

  buildPlayerUrl(publicUrl, mode = "mono") {
    const u = new URL("https://revi-vr-prod.web.app");
    u.searchParams.set("src", publicUrl || "");
    u.searchParams.set("mode", mode || "mono");
    return u.toString();
  }
}
